import { CurrencyPipe } from '@angular/common';
import {
    Directive,
    ElementRef,
    HostListener,
    afterRenderEffect,
    forwardRef,
    inject,
    input,
    output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[ldCurrencyMask]',
  providers: [
    CurrencyPipe,
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CurrencyMaskDirective), multi: true },
  ],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private readonly el = inject(ElementRef<HTMLInputElement>);
  private readonly currencyPipe = inject(CurrencyPipe);

  private isMovingCursor = false;

  value = input<number>(0);

  maxValue = input<number | null>(null);

  valueChange = output<number>();

  maxExceeded = output<boolean>();

  private onChange: ((value: number) => void) | null = null;
  private onTouched: (() => void) | null = null;
  private cvaValue: number | null = null;

  private get isCvaMode(): boolean {
    return this.onChange !== null;
  }

  private get currentValue(): number {
    return this.isCvaMode ? (this.cvaValue ?? 0) : this.value();
  }

  constructor() {
    afterRenderEffect({
      write: () => {
        if (!this.isCvaMode) {
          const formatted = this.currencyPipe.transform(this.value(), 'USD', 'symbol', '1.2-2');
          this.el.nativeElement.value = formatted || '$0.00';
          this.moveCursorToEnd();
        }
      },
    });
  }

  writeValue(value: number | null): void {
    this.cvaValue = value ?? 0;
    const formatted = this.currencyPipe.transform(this.cvaValue, 'USD', 'symbol', '1.2-2');
    this.el.nativeElement.value = formatted || '$0.00';
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'];

    if (allowedKeys.includes(event.key)) {
      if ((event.key === 'Backspace' || event.key === 'Delete') && this.currentValue === 0) {
        event.preventDefault();
      }
      return;
    }

    if (event.ctrlKey || event.metaKey) return;

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const max = this.maxValue();
    if (max !== null) {
      const currentDigits = this.el.nativeElement.value.replace(/\D/g, '');
      const prospectiveDigits = currentDigits + event.key;
      const prospectiveValue = parseInt(prospectiveDigits, 10) / 100;
      if (prospectiveValue > max) {
        this.maxExceeded.emit(true);
      }
    }
  }

  @HostListener('click')
  @HostListener('focus')
  onClick(): void {
    if (this.isMovingCursor) {
      return;
    }
    this.moveCursorToEnd();
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched?.();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const inputString = (event.target as HTMLInputElement).value;
    const digits = inputString.replace(/\D/g, '');
    const numericValue = digits ? parseInt(digits, 10) / 100 : 0;

    const max = this.maxValue();
    if (max !== null) {
      this.maxExceeded.emit(numericValue > max);
    }

    if (this.isCvaMode) {
      this.cvaValue = numericValue;
      this.onChange?.(numericValue);
    }

    this.valueChange.emit(numericValue);

    this.moveCursorToEnd();
  }

  private moveCursorToEnd(): void {
    const input = this.el.nativeElement;
    this.isMovingCursor = true;
    setTimeout(() => {
      const len = input.value.length;
      input.setSelectionRange(len, len);
      this.isMovingCursor = false;
    }, 0);
  }
}
